import { useMemo, useEffect, useState } from 'react';
import { API_URL } from '../config';

// Разрешения по умолчанию для каждой роли
const getDefaultPermissions = (role) => {
  const defaultPerms = {
    'admin': ['search-cloud', 'search-b24', 'cutting', 'drilling', 'zakalka', 'matovka', 'sclad', 'loft', 'upak',
              'cuttingb24', 'drillingb24', 'zakalkab24', 'matovkab24', 'scladb24', 'argonb24', 'loftb24',
              'upakb24', 'sendb24', 'driverb24', 'trip-management', 'visualloft', 'product-variations',
              'tovar', 'users', 'facetherminal', 'orders-dashboard', 'file-print', 'order-form', 'buhgalteriya'],
    'adminb24': ['search-cloud', 'search-b24', 'cutting', 'drilling', 'zakalka', 'matovka', 'sclad', 'loft', 'upak',
                 'cuttingb24', 'drillingb24', 'zakalkab24', 'matovkab24', 'scladb24', 'argonb24', 'loftb24',
                 'upakb24', 'sendb24', 'driverb24', 'trip-management', 'visualloft', 'product-variations',
                 'tovar', 'users', 'facetherminal', 'orders-dashboard', 'file-print', 'order-form', 'buhgalteriya'],
    'cutter': ['search-cloud', 'cutting'],
    'driller': ['search-cloud', 'drilling'],
    'zakalka': ['search-cloud', 'zakalka'],
    'matovka': ['search-cloud', 'matovka'],
    'sclad': ['search-cloud', 'sclad'],
    'loft': ['search-cloud', 'loft'],
    'upak': ['search-cloud', 'upak'],
    'cutterb24': ['search-b24', 'cuttingb24'],
    'drillerb24': ['search-b24', 'drillingb24'],
    'zakalkab24': ['search-b24', 'zakalkab24'],
    'matovkab24': ['search-b24', 'matovkab24'],
    'scladb24': ['search-b24', 'scladb24'],
    'loftb24': ['search-b24', 'loftb24'],
    'upakb24': ['search-b24', 'upakb24'],
    'sendb24': ['search-b24', 'sendb24'],
    'driverb24': ['search-b24', 'driverb24'],
    'designer': ['search-cloud', 'search-b24', 'visualloft'],
    'franchise': ['search-b24', 'visualloft'],
    'buhgalter': ['buhgalteriya'],
    'director': ['search-b24', 'buhgalteriya'],
    'initiator': ['buhgalteriya'],
  };
  return defaultPerms[role] || ['search-cloud', 'search-b24'];
};

export const usePermissions = () => {
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [isLoading, setIsLoading] = useState(true);

  // Проверяем актуальные разрешения при загрузке
  useEffect(() => {
    const checkUserPermissions = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('Checking permissions for user:', user.id, 'with token:', token ? 'present' : 'missing');
        
        if (!token || user.id === undefined || user.id === null) {
          console.log('No token or user ID, skipping permission check');
          setIsLoading(false);
          return;
        }

        const url = `${API_URL}/api/users/${user.id}`;
        console.log('Fetching user data from:', url);

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        console.log('Server response status:', response.status);

        if (response.ok) {
          const userData = await response.json();
          console.log('Server returned user data:', userData);
          
          // Парсим разрешения если они есть
          let serverPermissions = null;
          if (userData.permissions) {
            try {
              serverPermissions = JSON.parse(userData.permissions);
              console.log('Parsed server permissions:', serverPermissions);
            } catch (e) {
              console.log('Error parsing server permissions:', e);
            }
          } else {
            console.log('No permissions field in server response');
          }

          // Обновляем пользователя с актуальными разрешениями, ролью и локацией цеха
          const updatedUser = {
            ...user,
            role: userData.role || user.role,
            permissions: serverPermissions,
            workshopLocation: userData.workshopLocation !== undefined ? userData.workshopLocation : user.workshopLocation
          };

          // Обновляем localStorage только если данные изменились
          const currentUserStr = JSON.stringify(user);
          const updatedUserStr = JSON.stringify(updatedUser);
          
          console.log('Current user:', user);
          console.log('Updated user:', updatedUser);
          
          if (currentUserStr !== updatedUserStr) {
            localStorage.setItem('user', updatedUserStr);
            setUser(updatedUser);
            console.log('✅ Permissions updated from server:', serverPermissions);
          } else {
            console.log('No changes detected, keeping current permissions');
          }
        } else {
          const errorText = await response.text();
          console.log('Server error response:', errorText);
        }
      } catch (error) {
        console.log('Error checking user permissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserPermissions();
  }, [user.id]); // Зависит только от ID пользователя
  
  const userPermissions = useMemo(() => {
    // Если у пользователя есть кастомные разрешения, используем их
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions;
    }
    
    // Иначе используем разрешения по умолчанию для роли
    return getDefaultPermissions(user.role);
  }, [user.permissions, user.role]);
  
  const hasPermission = (permission) => {
    return userPermissions.includes(permission);
  };
  
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => userPermissions.includes(permission));
  };
  
  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => userPermissions.includes(permission));
  };

  return {
    permissions: userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    user,
    isLoading
  };
};

export default usePermissions;
